import { useLayoutEffect, useRef } from "react";
import { cn } from "../../lib/utils";
import { useIsMobileSidebar } from "../ClientLayout/AdminSidebar";
import {
  enhanceMobileTableCards,
  teardownMobileTableCards,
} from "./mobileTableCardEngine";

/**
 * Global table wrapper. On mobile, auto-converts nested `.data-table`
 * markup into collapse cards. On desktop, renders an unchanged wrap.
 *
 * @example
 * <DataTableCards fetching={isFetching}>
 *   <table className="data-table">...</table>
 * </DataTableCards>
 */
export default function DataTableCards({
  children,
  className,
  fetching = false,
  enabled,
}) {
  const rootRef = useRef(null);
  const isMobileViewport = useIsMobileSidebar();
  const active = enabled ?? isMobileViewport;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    if (!active) {
      teardownMobileTableCards(root);
      return undefined;
    }

    let frame = 0;
    let enhancing = false;

    const run = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (enhancing || !rootRef.current) return;
        enhancing = true;
        try {
          enhanceMobileTableCards(rootRef.current);
        } finally {
          enhancing = false;
        }
      });
    };

    run();

    const observer = new MutationObserver(() => {
      if (enhancing) return;
      run();
    });
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      teardownMobileTableCards(root);
    };
  }, [active]);

  return (
    <div
      ref={rootRef}
      className={cn(
        "data-table-wrap",
        active && "is-collapse-list",
        fetching && "is-fetching",
        className
      )}
    >
      {children}
    </div>
  );
}
